(ns mold-http.core
  (:require [day8.re-frame.http-fx]
            [ajax.core :as ajax]
            [re-frame.core :as re-frame]))


(defn reduce-kvs
  "Like `reduce-kv` but takes a flat sequence of kv pairs."
  [rf init kvs]
  (transduce (partition-all 2)
             (completing (fn [acc [k v]]
                           (rf acc k v)))
             init
             kvs))


(defn assoc-some
  "Assocs each kv if its value is not nil."
  ([m k v]
   (if (nil? v)
     (if (nil? m) {} m)
     (assoc m k v)))

  ([m k v & kvs]
   (reduce-kvs
     (fn [m k v]
       (if (nil? v) m (assoc m k v)))
     (assoc-some m k v)
     kvs))

  ([m kvs]
   (reduce-kv
     (fn [m k v]
       (if (nil? v) m (assoc m k v)))
     (if (nil? m) {} m)
     kvs)))


(defn concat-two-keywords
  "Create keyword from two keywords"
  ([first second]
   (concat-two-keywords first second "-"))

  ([first second delim]
   (let [name1 (name first)
         ns1   (namespace first)
         name2 (name second)]
     (keyword ns1 (str name1 delim name2)))))


(defn in-seq?
  "true if a sequence contains element"
  [seq elem]
  (some #(= elem %) seq))


(def noop
  (constantly nil))


(defonce config
  (atom nil))


(defn configure [options]
  (reset! config options))


(defn maybe-add-api-host
  "Prepends api host to requests starting with /"
  [url]
  (let [api-host (:api-host @config)]
    (if (and (some? api-host) (= \/ (first url)))
      (str api-host url)
      url)))


(defn api-request-map [{:keys [method url params success failure interceptors headers body format raw with-credentials]
                        :or   {method           :get
                               format           :json
                               with-credentials true
                               raw              false
                               body             nil}}]
  (assoc-some
    {:method          method
     :uri             (maybe-add-api-host url)
     :body            body
     :format          (case format
                        :json (ajax/json-request-format)
                        :url (ajax/url-request-format)
                        nil)
     :response-format (ajax/json-response-format {:keywords? true :raw raw})
     :on-success      success
     :on-failure      failure
     :with-credentials with-credentials
     :interceptors    interceptors
     :api (js/XMLHttpRequest.)}
    :headers headers
    :params params))


(defn add-http-event [event-name event-id]
  [[:http.events/add-event event-name event-id]])


(defn add-http-cache [cache-key status response]
  [[:http.cache/set cache-key status response]])


(defn add-cached-response [cofx cached-response xhrio-params]
  (let [{:keys [response status]} cached-response
        intended-cofx (get xhrio-params (concat-two-keywords :on status))
        cached-cofx [(conj intended-cofx response)]]
    (update cofx :dispatch-n concat cached-cofx)))


(defn reg-event-http
  [event-name
   request
   callback
   & {:keys [injected-cofx success-cofx failure-cofx authenticate? cache?]
      :or   {injected-cofx []
             success-cofx  []
             failure-cofx  []
             authenticate? true
             cache?        false}}]
  (let [success-key (concat-two-keywords event-name :success)
        failure-key (concat-two-keywords event-name :failure)
        complete-key (concat-two-keywords event-name :complete)
        inject-cofx [re-frame/trim-v]]

    (re-frame/reg-event-fx
      success-key
      [inject-cofx success-cofx]
      (fn [cofx [event-id cb-params result]]
        (let [new-cofx (callback cofx [nil result cb-params])]
          (cond-> new-cofx

                  (nil? (:dispatch-n new-cofx))
                  (assoc :dispatch-n [[complete-key event-id]])

                  (not (nil? (:dispatch-n new-cofx)))
                  (update :dispatch-n conj [complete-key event-id])

                  cache?
                  (update :dispatch-n concat (add-http-cache event-id :success result))))))

    (re-frame/reg-event-fx
      failure-key
      [inject-cofx failure-cofx]
      (fn [cofx [event-id cb-params error]]
        (let [error-status (get-in error [:parse-error :status])
              auth-failed? (and authenticate? (= 401 error-status))
              ;; Don't call the callback fn if it's auth failure, fail silently
              new-cofx (if auth-failed?
                         {}
                         (callback cofx [error nil cb-params]))
              unauthorized-event (get @config :unauthorized-event)]
          (cond-> new-cofx

                  (nil? (:dispatch-n new-cofx))
                  (assoc :dispatch-n [[complete-key event-id]])

                  (not (nil? (:dispatch-n new-cofx)))
                  (update :dispatch-n conj [complete-key event-id])

                  ;; 401 Unauthorized
                  (and auth-failed? (some? unauthorized-event))
                  (update :dispatch-n conj unauthorized-event)))))

    (re-frame/reg-event-fx
      complete-key
      [re-frame/trim-v]
      (fn [_ [event-id]]
        {:dispatch [:http.events/remove-event event-name event-id]}))

    (re-frame/reg-event-fx
      event-name
      [re-frame/trim-v injected-cofx]
      (fn [cofx [& args]]
        (when-let [request-cofx (request cofx args)]

          ;; Cached requests have to have the request id
          (when (and cache? (not (some? (get-in request-cofx [:http-xhrio :id]))))
            (js/console.warn (str "No id provided for " event-name " request. Caching will not work without a static http-xhrio id.")))

          (let [cb-params (-> request-cofx :http-xhrio :cb-params)
                event-id (keyword (or (get-in request-cofx [:http-xhrio :id])
                                      (str (random-uuid))))
                dynamic-xhrio-params {:success [success-key event-id cb-params]
                                      :failure [failure-key event-id cb-params]}
                get-auth-header (get @config :get-auth-header noop)
                auth-header (get-auth-header)
                xhrio-params (cond-> (:http-xhrio request-cofx)
                                     ;; register success/failure events
                                     :always (merge dynamic-xhrio-params)
                                     ;; add auth header if some
                                     (and authenticate? (some? auth-header))
                                     (assoc-in [:headers "Authorization"] auth-header)
                                     ;; get the full request
                                     :finally api-request-map)
                transact (add-http-event event-name event-id)
                cache-key event-id
                cached-response (when cache?
                                  @(re-frame/subscribe [:http.cache/get cache-key]))
                return-cached-version? (= (get cached-response :status) :success)]
            (cond-> request-cofx
                    return-cached-version? (dissoc :http-xhrio)
                    return-cached-version? (add-cached-response cached-response xhrio-params)
                    (not return-cached-version?) (assoc :http-xhrio xhrio-params)
                    (not return-cached-version?) (update :dispatch-n concat transact))))))))


(re-frame/reg-sub
 :http/events
 (fn [db [_ event-name]]
   (get-in db [:http/events event-name])))


(re-frame/reg-sub
  :mold-http/loading?
  (fn [[_ event-name]]
    [(re-frame/subscribe [:http/events event-name])
     (atom event-name)])
  (fn [[http-status event-name]]
    (seq http-status)))


(re-frame/reg-sub
  :mold-http/loading-id?
  (fn [[_ event-name event-id]]
    [(re-frame/subscribe [:http/events event-name])
     (atom event-id)
     (atom event-name)])
  (fn [[http-status event-id event-name]]
    (contains? http-status event-id)))


(re-frame/reg-sub
  :http.cache/get
  (fn [db [_ cache-key]]
    (get-in db [:http/caches cache-key])))


(re-frame/reg-event-db
  :mold-http/clear-caches
  (fn [db]
    (dissoc db :http/caches)))



(re-frame/reg-event-db
 :http.events/add-event
 (fn [db [_ event-name event-id]]
   (let [ids (get-in db [:http/events event-name])
         new-ids (set (conj ids event-id))]
     (assoc-in db [:http/events event-name] new-ids))))

(re-frame/reg-event-db
 :http.events/remove-event
 (fn [db [_ event-name event-id]]
   (let [ids (get-in db [:http/events event-name])
         new-ids (set (disj ids event-id))]
     (assoc-in db [:http/events event-name] new-ids))))


(re-frame/reg-event-db
 :http.cache/set
 (fn [db [_ cache-key status response]]
   (assoc-in db [:http/caches cache-key] {:status status :response response})))


(re-frame/reg-event-db
 :http.cache/clear
 (fn [db [_ cache-key]]
   (update-in db [:http/caches] dissoc cache-key)))

